import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, SITE } from "../lib/categories";
import { subscribeNewsletter, formatApiErrorDetail } from "../lib/api";
import { Send } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setMsg("");
    try {
      const r = await subscribeNewsletter(email);
      setMsg(r.message || "Subscribed.");
      setEmail("");
    } catch (err) {
      setMsg(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#121212] text-white mt-24" data-testid="site-footer">
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="ceg-container py-16 grid md:grid-cols-2 gap-10 items-end">
          <div>
            <p className="ceg-overline">Newsletter · Weekly</p>
            <h2 className="font-['Outfit'] font-black tracking-tighter text-4xl md:text-5xl mt-2">
              Get a week of Chicago<br />electronics intelligence.
            </h2>
            <p className="mt-4 text-white/70 max-w-md">
              One email. Resale prices, buyer alerts, recycling events. No spam, no nonsense.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex flex-col gap-3" data-testid="newsletter-form">
            <div className="flex">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-transparent border border-white px-4 py-3 outline-none focus:border-[#FF3823]"
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-[#FF3823] text-white px-5 py-3 font-semibold uppercase tracking-wider text-sm hover:bg-white hover:text-[#121212] transition-colors flex items-center gap-2 disabled:opacity-60"
                data-testid="newsletter-submit"
              >
                {loading ? "..." : (<><Send size={16} /> Subscribe</>)}
              </button>
            </div>
            {msg && <p className="text-sm text-white/80" data-testid="newsletter-message">{msg}</p>}
          </form>
        </div>
      </div>

      <div className="ceg-container py-16 grid md:grid-cols-12 gap-10 border-b border-white/10">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="font-black text-3xl tracking-tighter leading-none">
            Chicago<br />Electronics<span className="text-[#FF3823]">.</span>
          </div>
          <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-sm">
            {SITE.description}
          </p>
        </div>

        {/* Categories */}
        <div className="md:col-span-4">
          <p className="ceg-overline mb-4">Categories</p>
          <ul className="grid grid-cols-2 gap-y-2 text-sm">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/category/${c.slug}`} className="text-white/80 hover:text-[#FF3823] transition-colors" data-testid={`footer-cat-${c.slug}`}>
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        <div className="md:col-span-4">
          <p className="ceg-overline mb-4">Resources</p>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/directory" className="text-white/80 hover:text-[#FF3823] transition-colors" data-testid="footer-directory">
                Chicago Recycling & Repair Directory
              </Link>
            </li>
            <li>
              <Link to="/about" className="text-white/80 hover:text-[#FF3823] transition-colors" data-testid="footer-resources-about">
                Our editorial standards
              </Link>
            </li>
            <li>
              <Link to="/contact" className="text-white/80 hover:text-[#FF3823] transition-colors" data-testid="footer-resources-contact">
                Submit a tip
              </Link>
            </li>
          </ul>
          <p className="text-xs text-white/50 mt-4 leading-relaxed">
            Independent guidance. We don't accept payment for coverage or placement.
          </p>
        </div>
      </div>

      <div className="ceg-container py-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs uppercase tracking-wider text-white/60">
        <p>© {new Date().getFullYear()} Chicago Electronics Guide · Independent editorial.</p>
        <div className="flex gap-5">
          <Link to="/about" className="hover:text-[#FF3823]" data-testid="footer-about">About</Link>
          <Link to="/contact" className="hover:text-[#FF3823]" data-testid="footer-contact">Contact</Link>
          <Link to="/privacy" className="hover:text-[#FF3823]" data-testid="footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-[#FF3823]" data-testid="footer-terms">Terms</Link>
          <a href="/api/sitemap.xml" className="hover:text-[#FF3823]" data-testid="footer-sitemap">Sitemap</a>
        </div>
      </div>
    </footer>
  );
}
